import { useMemo } from "react";
import { useApiClient } from "../apiClients";
import { ENDPOINTS_PAIEMENT } from "./endpoints";

// Administration B2B : contrats écoles, prix négociés, seuils et abonnements.
//
// Les champs dérivés (sieges_actifs, montant_sieges, depassement,
// tilmischool_gratuit) sont calculés par le serveur et jamais recalculés ici :
// l'écran doit afficher exactement ce qui sera facturé.

/**
 * Un établissement proposable à la création d'un contrat.
 * Remplace la saisie d'un identifiant numérique : se tromper d'ID rattacherait
 * la facturation au mauvais établissement sans que rien ne le signale.
 */
export type TOrganisationOption = {
  id: number;
  login: string;
  nom: string;
  /** Un contrat existe déjà — affiché quand même, pour pouvoir le corriger. */
  deja_sous_contrat: boolean;
};

export type TEcoleB2B = {
  promo_code_id: number;
  organization_id: number;
  organization_login: string;
  code: string;
  prix_siege: number;
  prix_tilmischool?: number;
  /** « ecole » : l'établissement règle, le parent ne paie rien. « parent » : le parent paie au tarif négocié. */
  mode_paiement: string;
  devise: string;
  date_debut?: string;
  date_fin?: string;
  seuil_gratuite?: number;
  actif: boolean;
  sieges_actifs: number;
  effectif_ecole: number;
  /** Plus de sièges consommés que d'élèves inscrits : signalement, pas blocage. */
  depassement: boolean;
  montant_sieges: number;
  tilmischool_gratuit: boolean;
};

// seuil_gratuite est optionnel et distingue « absent » de « zéro » côté
// serveur : ne l'envoyer que lorsqu'il est réellement saisi.
export type TEcoleB2BUpsert = {
  organization_id: number;
  code: string;
  prix_siege: number;
  prix_tilmischool?: number;
  devise: string;
  date_debut?: string;
  date_fin?: string;
  seuil_gratuite?: number;
  actif?: boolean;
  /** « ecole » ou « parent ». Vide = « parent », le mode qui exige un paiement. */
  mode_paiement?: string;
};

export type TAbonnementOrg = {
  id: number;
  organization_id: number;
  offre_code: string;
  vendor: string;
  prix_unitaire: number;
  devise: string;
  /** Tenue par le recomptage serveur, jamais saisie à la main. */
  quantite: number;
  ls_subscription_id?: string;
  ls_subscription_item_id?: string;
  statut: string;
  derniere_synchro?: string;
};

export type TAbonnementUpsert = {
  organization_id: number;
  offre_code: string;
  vendor: string;
  prix_unitaire: number;
  devise: string;
  ls_subscription_id?: string;
  ls_subscription_item_id?: string;
  statut?: string;
};

export type TOffre = {
  code: string;
  libelle: string;
  app_codes: string[];
  portee: string;
  actif: boolean;
};

// Une école signale un élève qu'elle ne reconnaît pas ; elle ne coupe rien.
// Seule l'administration Tilmicode peut retirer un siège.
export type TSignalement = {
  id: number;
  id_billing: number;
  student_id: number;
  org_promo_code_id: number;
  organization_id: number;
  motif?: string;
  statut: 'en_attente' | 'retire' | 'rejete';
  signale_par?: number;
  traite_par?: number;
  traite_at?: string;
  commentaire_admin?: string;
  created_at: string;
};

export const useB2BApi = () => {
  const client = useApiClient("paiement");

  return useMemo(
    () => ({
      listOrganisations: async (): Promise<TOrganisationOption[]> => {
        const { data } = await client.get<{ items: TOrganisationOption[] }>(
          ENDPOINTS_PAIEMENT.B2B.ORGANISATIONS
        );
        return data?.items ?? [];
      },
      listEcoles: async (): Promise<TEcoleB2B[]> => {
        const { data } = await client.get<{ items: TEcoleB2B[] }>(ENDPOINTS_PAIEMENT.B2B.ECOLES);
        return data.items ?? [];
      },
      upsertEcole: (payload: TEcoleB2BUpsert) =>
        client.post(ENDPOINTS_PAIEMENT.B2B.ECOLES, payload),
      deleteEcole: (id: number) => client.delete(ENDPOINTS_PAIEMENT.B2B.ECOLE(id)),
      listAbonnements: async (orgID: number): Promise<TAbonnementOrg[]> => {
        const { data } = await client.get<{ items: TAbonnementOrg[] }>(
          ENDPOINTS_PAIEMENT.B2B.ABONNEMENTS_ECOLE(orgID)
        );
        return data.items ?? [];
      },
      upsertAbonnement: (payload: TAbonnementUpsert) =>
        client.post(ENDPOINTS_PAIEMENT.B2B.ABONNEMENTS, payload),
      listOffres: async (): Promise<TOffre[]> => {
        const { data } = await client.get<{ items: TOffre[] }>(ENDPOINTS_PAIEMENT.B2B.OFFRES);
        return data.items ?? [];
      },
      listSignalements: async (orgID: number, statut?: string): Promise<TSignalement[]> => {
        const url = ENDPOINTS_PAIEMENT.B2B.SIGNALEMENTS(orgID);
        const { data } = await client.get<{ items: TSignalement[] }>(
          statut ? `${url}?statut=${statut}` : url
        );
        return data.items ?? [];
      },
      /**
       * Retirer exclut le siège du renouvellement : l'élève garde son accès
       * jusqu'à la fin de la période payée, et le mois entamé reste dû.
       */
      traiterSignalement: (id: number, retirer: boolean, commentaire?: string) =>
        client.post(ENDPOINTS_PAIEMENT.B2B.TRAITER_SIGNALEMENT(id), {
          retirer,
          commentaire: commentaire ?? '',
        }),
      /** Recompte les quantités hors du rythme du scheduler (clôture de mois). */
      sync: () => client.post(ENDPOINTS_PAIEMENT.B2B.SYNC),
    }),
    [client]
  );
};
